import { createTheme, ThemeOptions } from '@mui/material/styles';

// Light theme configuration
const lightThemeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: '#000000',
      light: '#333333',
      dark: '#000000',
    },
    secondary: {
      main: '#666666',
      light: '#999999',
      dark: '#333333',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#000000',
      secondary: '#666666',
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    divider: 'rgba(0, 0, 0, 0.08)',
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  typography: {
    fontFamily: '"Roboto Mono", "Courier New", monospace',
    h1: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 300,
      fontSize: '4rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      '@media (max-width:600px)': {
        fontSize: '2.5rem',
        lineHeight: 1.1,
        letterSpacing: '-0.01em',
      },
      '@media (min-width:601px) and (max-width:960px)': {
        fontSize: '3.25rem',
        lineHeight: 1.15,
      },
    },
    h2: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 400,
      fontSize: '3rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      '@media (max-width:600px)': {
        fontSize: '2rem',
        lineHeight: 1.2,
        letterSpacing: '-0.005em',
      },
      '@media (min-width:601px) and (max-width:960px)': {
        fontSize: '2.5rem',
        lineHeight: 1.25,
      },
    },
    h3: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 500,
      fontSize: '2.25rem',
      lineHeight: 1.4,
      letterSpacing: '-0.005em',
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
        lineHeight: 1.3,
        letterSpacing: '0em',
      },
      '@media (min-width:601px) and (max-width:960px)': {
        fontSize: '2rem',
        lineHeight: 1.35,
      },
    },
    h4: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 500,
      fontSize: '1.75rem',
      lineHeight: 1.4,
      letterSpacing: '0em',
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
        lineHeight: 1.3,
      },
      '@media (min-width:601px) and (max-width:960px)': {
        fontSize: '1.625rem',
        lineHeight: 1.35,
      },
    },
    h5: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 500,
      fontSize: '1.375rem',
      lineHeight: 1.5,
      letterSpacing: '0em',
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
        lineHeight: 1.4,
      },
      '@media (min-width:601px) and (max-width:960px)': {
        fontSize: '1.3125rem',
        lineHeight: 1.45,
      },
    },
    h6: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 500,
      fontSize: '1.125rem',
      lineHeight: 1.5,
      letterSpacing: '0.007em',
      '@media (max-width:600px)': {
        fontSize: '1rem',
        lineHeight: 1.4,
      },
      '@media (min-width:601px) and (max-width:960px)': {
        fontSize: '1.0625rem',
        lineHeight: 1.45,
      },
    },
    body1: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 400,
      fontSize: '1.125rem',
      lineHeight: 1.7,
      letterSpacing: '0.009em',
      '@media (max-width:600px)': {
        fontSize: '1rem',
        lineHeight: 1.6,
        letterSpacing: '0.007em',
      },
      '@media (min-width:601px) and (max-width:960px)': {
        fontSize: '1.0625rem',
        lineHeight: 1.65,
      },
    },
    body2: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.6,
      letterSpacing: '0.009em',
      '@media (max-width:600px)': {
        fontSize: '0.9375rem',
        lineHeight: 1.5,
        letterSpacing: '0.007em',
      },
      '@media (min-width:601px) and (max-width:960px)': {
        fontSize: '0.96875rem',
        lineHeight: 1.55,
      },
    },
    button: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.75,
      letterSpacing: '0.028em',
      textTransform: 'none',
      '@media (max-width:600px)': {
        fontSize: '0.8125rem',
        lineHeight: 1.7,
      },
    },
    subtitle1: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 400,
      fontSize: '1.25rem',
      lineHeight: 1.6,
      letterSpacing: '0.009em',
      '@media (max-width:600px)': {
        fontSize: '1.125rem',
        lineHeight: 1.5,
        letterSpacing: '0.007em',
      },
      '@media (min-width:601px) and (max-width:960px)': {
        fontSize: '1.1875rem',
        lineHeight: 1.55,
      },
    },
    subtitle2: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.57,
      letterSpacing: '0.007em',
      '@media (max-width:600px)': {
        fontSize: '0.8125rem',
        lineHeight: 1.5,
      },
      '@media (min-width:601px) and (max-width:960px)': {
        fontSize: '0.84375rem',
        lineHeight: 1.53,
      },
    },
    caption: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 1.5,
      letterSpacing: '0.033em',
      '@media (max-width:600px)': {
        fontSize: '0.6875rem',
        lineHeight: 1.4,
      },
    },
    overline: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 2.66,
      letterSpacing: '0.083em',
      textTransform: 'uppercase',
      '@media (max-width:600px)': {
        fontSize: '0.6875rem',
        lineHeight: 2.5,
      },
    },
  },
  shape: {
    borderRadius: 16,
  },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          fontWeight: 500,
          fontSize: '0.875rem',
          padding: '12px 24px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '@media (max-width:600px)': {
            fontSize: '0.8125rem',
            padding: '10px 20px',
          },
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
            transform: 'translateY(-1px)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          border: '1px solid rgba(0, 0, 0, 0.04)',
          '&:hover': {
            transform: 'translateY(-6px)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
            border: '1px solid rgba(0, 0, 0, 0.2)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          // Remove global gradient override to let individual components handle their own gradients
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontWeight: 500,
          fontSize: '0.75rem',
          '@media (max-width:600px)': {
            fontSize: '0.6875rem',
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        },
      },
    },
  },
};

// Dark theme configuration
const darkThemeOptions: ThemeOptions = {
  palette: {
    mode: 'dark',
    primary: {
      main: '#ffffff',
      light: '#ffffff',
      dark: '#cccccc',
    },
    secondary: {
      main: '#999999',
      light: '#cccccc',
      dark: '#666666',
    },
    background: {
      default: '#000000',
      paper: '#1a1a1a',
    },
    text: {
      primary: '#ffffff',
      secondary: '#999999',
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  typography: {
    fontFamily: '"Roboto Mono", "Courier New", monospace',
    h1: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 300,
      fontSize: '4rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      '@media (max-width:600px)': {
        fontSize: '2.5rem',
        lineHeight: 1.1,
        letterSpacing: '-0.01em',
      },
      '@media (min-width:601px) and (max-width:960px)': {
        fontSize: '3.25rem',
        lineHeight: 1.15,
      },
    },
    h2: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 400,
      fontSize: '3rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      '@media (max-width:600px)': {
        fontSize: '2rem',
        lineHeight: 1.2,
        letterSpacing: '-0.005em',
      },
      '@media (min-width:601px) and (max-width:960px)': {
        fontSize: '2.5rem',
        lineHeight: 1.25,
      },
    },
    h3: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 500,
      fontSize: '2.25rem',
      lineHeight: 1.4,
      letterSpacing: '-0.005em',
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
        lineHeight: 1.3,
        letterSpacing: '0em',
      },
      '@media (min-width:601px) and (max-width:960px)': {
        fontSize: '2rem',
        lineHeight: 1.35,
      },
    },
    h4: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 500,
      fontSize: '1.75rem',
      lineHeight: 1.4,
      letterSpacing: '0em',
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
        lineHeight: 1.3,
      },
      '@media (min-width:601px) and (max-width:960px)': {
        fontSize: '1.625rem',
        lineHeight: 1.35,
      },
    },
    h5: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 500,
      fontSize: '1.375rem',
      lineHeight: 1.5,
      letterSpacing: '0em',
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
        lineHeight: 1.4,
      },
      '@media (min-width:601px) and (max-width:960px)': {
        fontSize: '1.3125rem',
        lineHeight: 1.45,
      },
    },
    h6: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 500,
      fontSize: '1.125rem',
      lineHeight: 1.5,
      letterSpacing: '0.007em',
      '@media (max-width:600px)': {
        fontSize: '1rem',
        lineHeight: 1.4,
      },
      '@media (min-width:601px) and (max-width:960px)': {
        fontSize: '1.0625rem',
        lineHeight: 1.45,
      },
    },
    body1: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 400,
      fontSize: '1.125rem',
      lineHeight: 1.7,
      letterSpacing: '0.009em',
      '@media (max-width:600px)': {
        fontSize: '1rem',
        lineHeight: 1.6,
        letterSpacing: '0.007em',
      },
      '@media (min-width:601px) and (max-width:960px)': {
        fontSize: '1.0625rem',
        lineHeight: 1.65,
      },
    },
    body2: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.6,
      letterSpacing: '0.009em',
      '@media (max-width:600px)': {
        fontSize: '0.9375rem',
        lineHeight: 1.5,
        letterSpacing: '0.007em',
      },
      '@media (min-width:601px) and (max-width:960px)': {
        fontSize: '0.96875rem',
        lineHeight: 1.55,
      },
    },
    button: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.75,
      letterSpacing: '0.028em',
      textTransform: 'none',
      '@media (max-width:600px)': {
        fontSize: '0.8125rem',
        lineHeight: 1.7,
      },
    },
    subtitle1: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 400,
      fontSize: '1.25rem',
      lineHeight: 1.6,
      letterSpacing: '0.009em',
      '@media (max-width:600px)': {
        fontSize: '1.125rem',
        lineHeight: 1.5,
        letterSpacing: '0.007em',
      },
      '@media (min-width:601px) and (max-width:960px)': {
        fontSize: '1.1875rem',
        lineHeight: 1.55,
      },
    },
    subtitle2: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.57,
      letterSpacing: '0.007em',
      '@media (max-width:600px)': {
        fontSize: '0.8125rem',
        lineHeight: 1.5,
      },
      '@media (min-width:601px) and (max-width:960px)': {
        fontSize: '0.84375rem',
        lineHeight: 1.53,
      },
    },
    caption: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 1.5,
      letterSpacing: '0.033em',
      '@media (max-width:600px)': {
        fontSize: '0.6875rem',
        lineHeight: 1.4,
      },
    },
    overline: {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 2.66,
      letterSpacing: '0.083em',
      textTransform: 'uppercase',
      '@media (max-width:600px)': {
        fontSize: '0.6875rem',
        lineHeight: 2.5,
      },
    },
  },
  shape: {
    borderRadius: 16,
  },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          fontWeight: 500,
          fontSize: '0.875rem',
          padding: '12px 24px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '@media (max-width:600px)': {
            fontSize: '0.8125rem',
            padding: '10px 20px',
          },
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
            transform: 'translateY(-1px)',
          },
        },
        contained: {
          backgroundColor: '#000000',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#1a1a1a',
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.5)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          '&:hover': {
            transform: 'translateY(-6px)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(20px)',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          // Remove global gradient override to let individual components handle their own gradients
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontWeight: 500,
          fontSize: '0.75rem',
          '@media (max-width:600px)': {
            fontSize: '0.6875rem',
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
        },
      },
    },
  },
};

export const lightTheme = createTheme(lightThemeOptions);
export const darkTheme = createTheme(darkThemeOptions);

// Default export for backward compatibility
export const theme = lightTheme;